import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addYears, isEqual, startOfMinute } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { Event, PostEvent, PutEvent } from 'src/app/interfaces/api/event';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { EventService } from 'src/app/services/api/events/event.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { ButtonComponent, DatePickerComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-invite-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    TranslateModule,
    SearchBarComponent,
    MatIconModule,
    MatFormFieldModule,
    ButtonComponent,
    SpinnerComponent,
    NoDataComponent,
    DatePickerComponent,
    MatButtonToggleModule
  ],
  templateUrl: './invite-user.component.html',
  styleUrls: ['./invite-user.component.scss']
})

export class InviteUserComponent implements AfterViewInit, OnDestroy {
  loading: boolean = false;
  @ViewChild('userPaginator') userPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  users: User[] = [];
  usersCount: number = 0;
  usersData: MatTableDataSource<User> = new MatTableDataSource();
  usersColumns: string[] = ['user', 'role', 'actions'];
  searchText: string = '';
  project: Project = defaultProject;
  private readonly destroy$ = new Subject<void>();
  requestCancel$: Subject<void> = new Subject<void>();
  startDate: string = new Date().toISOString();
  now: string = new Date().toISOString();
  maxDate: string = addYears(new Date(), 1).toISOString();
  user!: User;
  isMobile: boolean = false;
  tracker?: Tracker;
  event?: Event;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { project: Project, user: User, isMobile: boolean, tracker?: Tracker, event?: Event },
    private userService: UserService,
    private eventService: EventService,
    private ws: WebSocketService,
    private dialogRef: MatDialogRef<InviteUserComponent>,
    public dialog: MatDialog,
    private pop: Pop,
    private translate: TranslateService
  ) {
    this.project = data.project;
    this.user = data.user;
    this.isMobile = data.isMobile;
    this.tracker = data.tracker;
    this.event = data.event;
    if (this.isTrackerVisit()) this.usersColumns = ['user', 'actions'];
    if (this.event?.startDate)
      this.startDate = new Date(this.event.startDate).toISOString();
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.loadUsers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    if (text !== this.searchText) {
      this.searchText = text;
      this.userPaginator.pageIndex = 0;
      this.loadUsers();
    }
  }

  loadUsers(): void {
    this.requestCancel$.next();
    this.loading = true;

    const query: any = {
      offset: this.userPaginator ? this.userPaginator.pageIndex * this.userPaginator.pageSize : 0,
      limit: this.userPaginator ? this.userPaginator.pageSize : 10,
    };

    const searchText = this.searchText && this.searchText.trim() !== '' ? this.searchText.trim() : "";

    this.userService.searchAndCountUsers(searchText, query).pipe(takeUntil(this.requestCancel$)).subscribe({
      next: (res: { results: User[], total: number }) => {
        this.users = res.results.map(u => ({
          ...u,
          invited: this.event ? (this.event.user == u.id || this.event.message["users"].includes(u.id)) : false,
          hseRole: this.getRole(u)
        }));

        this.users.sort((a, b) => {
          if (a.invited != b.invited) return a.invited ? -2 : 1;
          return a.email.localeCompare(b.email)
        }
        );
        this.users = this.users.filter(u => u.id != this.user.id);

        this.usersData.data = this.users;
        this.usersCount = res.total;

        if (this.userPaginator)
          this.userPaginator.length = res.total;

        this.usersData.sort = this.sort;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      }
    });
  }

  save(): void {
    this.dialogRef.close();
  }

  handlePageEvent(e: PageEvent) {
    this.loadUsers();
  }

  onDatePickerChanged(newDate: string): void {
    const incoming = startOfMinute(new Date(newDate));
    const existing = startOfMinute(new Date(this.startDate));
    if (isEqual(incoming, existing)) return;
    this.startDate = incoming.toISOString();
  }

  onDatePickerClosed(): void {
    if (!this.event) return;


    const serverDate = startOfMinute(new Date(this.event.startDate));
    const pendingDate = startOfMinute(new Date(this.startDate));
    if (isEqual(pendingDate, serverDate)) return;

    this.eventService.updateStreamEvent({ id: this.event.id, date: this.startDate })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.event!.startDate = this.startDate;
          this.pop.success(this.translate.instant('stream.invite.date_updated'));
        },
        error: () => {
          this.startDate = this.event!.startDate as string;
          this.pop.error(this.translate.instant('stream.invite.error'));
        }
      });
  }

  invite(user: User): void {
    if (!user || !this.event) return;
    user.invited = true;
    const isPublisher = user.hseRole === 'publisher';

    if (isPublisher) {
      this.event.user = user.id;
      // Frontend display field (so the operator appears without a page refresh).
      this.event.host = user as any;
    } else {
      this.event.message["users"] = [...(this.event.message["users"] || []), user.id];
      // Frontend display field (so the expert appears without a page refresh).
      this.event.invited = [...(this.event.invited || []), user as any];
    }

    const putEvent = {
      id: this.event.id,
      invitedId: user.id,
      isPublisher,
      date: this.startDate
    };

    this.eventService.updateStreamEvent(putEvent).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.pop.success(this.translate.instant('stream.invite.success', { email: user.email }));
        this.ws.streamEvent('user-added', {
          addedUser: { id: user.id, name: `${user.firstName} ${user.lastName}`, firstName: user.firstName, lastName: user.lastName, email: user.email, avatar: user.avatar || '' },
          role: isPublisher ? 'publisher' : 'viewer',
        });
      },
      error: () => {
        user.invited = false;
        this.pop.error(this.translate.instant('stream.invite.error'));
      }
    });
  }

  copyLink(): void {
    if (!this.event) return;
    const url = `${window.location.origin}/project/stream?project=${this.project.id}&event=${this.event.id}&publisher=${this.isPublisherTaken() || this.isTrackerVisit() ? false : true}`;
    navigator.clipboard.writeText(url).then(() => {
      this.pop.success(this.translate.instant('stream.invite.link_copied'));
    });
  }

  close() {
    this.dialogRef.close()
  }

  isPublisherTaken(): boolean {
    return !!this.event?.user;
  }

  isTrackerVisit(): boolean {
    return this.event?.message?.["stream"] === 'tracker';
  }

  getRole(user: User) {
    if (this.isTrackerVisit()) return 'viewer';
    if (this.event?.user == user.id) return 'publisher';
    if (this.event?.message && this.event.message["users"].includes(user.id)) return 'viewer';
    if (this.event?.user) return 'viewer';
    return 'publisher';
  }
}
